import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import toast from 'react-hot-toast';
import { ShoppingCart, Minus, Plus, Trash2, Loader2, Package, ListOrdered } from 'lucide-react';
import { mediaUrl } from '../utils/media';
import { curLabel } from '../utils/currency';
import { useT } from '../utils/translate';
import { setLine, removeLine, clearCart } from '../store/cartSlice';

const API = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

function fmt(n) {
  return Number(n || 0).toLocaleString('en-US');
}

// Savat / buyurtma yuborish. Narx faqat ko'rsatish uchun — yakuniy narx va
// miqdor server tomonidan (`pickCustomerPrice`) qayta hisoblanadi, shu bois
// bu yerda hisoblangan jami taxminiy bo'lishi mumkin.
export default function CartPage() {
  const t = useT();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const items = useSelector(s => s.cart.items);
  const [note, setNote] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const byCurrency = {};
  items.forEach(i => { byCurrency[i.currency] = (byCurrency[i.currency] || 0) + i.price * i.qty; });

  const submit = async () => {
    if (!items.length) return;
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/client/orders`, {
        items: items.map(i => ({ product: i.productId, unit: i.unitKey, qty: i.qty })),
        note: note.trim(),
      });
      dispatch(clearCart());
      toast.success(t('cart.orderSent', { number: res.data.order?.number }));
      navigate('/orders');
    } catch (error) {
      toast.error(error.response?.data?.message || t('cart.orderError'));
    } finally {
      setSubmitting(false);
    }
  };

  if (!items.length) {
    return (
      <div className="flex flex-col items-center justify-center py-24 px-4 text-center">
        <ShoppingCart className="w-10 h-10 text-ink-tertiary mb-3" />
        <p className="text-sm text-ink-tertiary">{t('cart.empty')}</p>
        <div className="flex items-center gap-2 mt-4">
          <button onClick={() => navigate('/catalog')} className="btn-primary btn-md">{t('cart.goCatalog')}</button>
          <button onClick={() => navigate('/orders')} className="btn-secondary btn-md">{t('cart.myOrders')}</button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      <div className="shrink-0 flex items-center justify-between px-3 pt-3">
        <h1 className="text-lg font-bold text-ink">{t('cart.title')}</h1>
        <button onClick={() => navigate('/orders')}
          className="flex items-center gap-1 text-xs font-medium text-primary-600 bg-primary-50 px-3 py-1.5 rounded-full">
          <ListOrdered className="w-3.5 h-3.5" /> {t('cart.myOrders')}
        </button>
      </div>
      <div className="flex-1 overflow-y-auto p-3 space-y-2">
        {items.map(i => (
          <div key={`${i.productId}:${i.unitKey}`} className="rounded-xl border border-surface-200 bg-white p-2 flex items-center gap-3">
            <div className="w-12 h-12 rounded-lg bg-surface-100 flex items-center justify-center overflow-hidden shrink-0">
              {i.image ? <img src={mediaUrl(i.image)} alt="" className="w-full h-full object-cover" /> : <Package className="w-5 h-5 text-ink-tertiary" />}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-ink leading-tight line-clamp-2">{i.name}</p>
              <p className="text-xs text-ink-tertiary mt-0.5">{fmt(i.price)} {curLabel(i.currency)}{i.unitLabel ? ` / ${i.unitLabel}` : ''}</p>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <div className="flex items-center gap-1 rounded-full bg-surface-100 p-1">
                <button onClick={() => dispatch(setLine({ ...i, qty: i.qty - 1 }))}
                  className="w-7 h-7 flex items-center justify-center rounded-full bg-white text-ink-secondary shadow-sm active:scale-95 transition-transform">
                  <Minus className="w-3.5 h-3.5" />
                </button>
                <span className="text-sm font-semibold text-ink w-6 text-center">{i.qty}</span>
                <button onClick={() => dispatch(setLine({ ...i, qty: i.qty + 1 }))}
                  className="w-7 h-7 flex items-center justify-center rounded-full bg-white text-ink-secondary shadow-sm active:scale-95 transition-transform">
                  <Plus className="w-3.5 h-3.5" />
                </button>
              </div>
              <button onClick={() => dispatch(removeLine({ productId: i.productId, unitKey: i.unitKey }))}
                className="p-2 rounded-lg text-red-500 hover:bg-red-50 active:scale-95 transition-colors">
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          </div>
        ))}

        <textarea value={note} onChange={e => setNote(e.target.value)} placeholder={t('cart.notePlaceholder')}
          rows={2} className="input w-full resize-none mt-2" />
      </div>

      <div className="shrink-0 border-t border-surface-200 bg-white p-3 safe-bottom space-y-2">
        {Object.entries(byCurrency).map(([cur, sum]) => (
          <div key={cur} className="flex items-center justify-between text-sm">
            <span className="text-ink-tertiary">{t('cart.total')} ({curLabel(cur)})</span>
            <span className="font-bold text-ink">{fmt(sum)}</span>
          </div>
        ))}
        <button onClick={submit} disabled={submitting} className="btn-primary btn-lg w-full">
          {submitting ? <><Loader2 className="w-4 h-4 animate-spin" /> {t('cart.submitting')}</> : t('cart.submit')}
        </button>
      </div>
    </div>
  );
}
